import React, { useMemo } from "react";
import { useSimulationData } from "../../../../hooks/useSimulationData";
import { formatWithCommas } from "../../utils/formatNumber";

const MAX_DISPLAY_ROWS = 100;

const sumColumn = (rows, col) => {
  let sum = 0;
  let numericCount = 0;
  for (let i = 0; i < rows.length; i += 1) {
    const raw = rows[i][col];
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number.parseFloat(raw);
    if (Number.isFinite(n)) {
      sum += n;
      numericCount += 1;
    }
  }
  return numericCount > 0 ? sum : null;
};

export default function TimeseriesRawTable() {
  const { allTimeData, isLoading, error } = useSimulationData();

  const columns = useMemo(
    () => (allTimeData && allTimeData[0] ? Object.keys(allTimeData[0]) : []),
    [allTimeData],
  );

  const columnTotals = useMemo(() => {
    if (!allTimeData || allTimeData.length === 0) return {};
    const totals = {};
    for (const col of columns) {
      totals[col] = sumColumn(allTimeData, col);
    }
    return totals;
  }, [allTimeData, columns]);

  const displayRows = useMemo(
    () => (allTimeData || []).slice(0, MAX_DISPLAY_ROWS),
    [allTimeData],
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <p className="verification-raw-message">Loading timeseries data...</p>
      );
    }
    if (error) {
      return (
        <p className="verification-raw-message verification-raw-message--error">
          {error}
        </p>
      );
    }
    if (!allTimeData || allTimeData.length === 0) {
      return (
        <p className="verification-raw-message">
          No timeseries data available for the selected simulation.
        </p>
      );
    }

    return (
      <>
        <div className="verification-raw-scroll">
          <table className="verification-raw-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col} scope="col">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="verification-raw-row--total">
                {columns.map((col, idx) => {
                  const total = columnTotals[col];
                  if (idx === 0) {
                    return (
                      <td key={col} className="verification-raw-total-label">
                        TOTAL
                      </td>
                    );
                  }
                  return (
                    <td key={col}>
                      {total === null || total === undefined
                        ? ""
                        : formatWithCommas(total)}
                    </td>
                  );
                })}
              </tr>
              {displayRows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {columns.map((col) => (
                    <td key={col}>{row[col] ?? ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {allTimeData.length > MAX_DISPLAY_ROWS ? (
          <p className="verification-raw-caption">
            Showing first {MAX_DISPLAY_ROWS.toLocaleString()} of{" "}
            {allTimeData.length.toLocaleString()} rows.
          </p>
        ) : null}
      </>
    );
  };

  return (
    <section className="verification-section verification-raw-section">
      <h2 className="verification-section-title">Timeseries Raw Data</h2>
      {renderBody()}
    </section>
  );
}
